import { useEffect, useRef, useState } from "react";
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useActiveOrg } from "common-fe";
import { cleanStationName, suggestStationName } from "score-common";
import { useScoreContext } from "../context/ScoreContext";
import { useStationIdentity } from "../hooks/useStationIdentity";
import { canPersistStationIdentity, readAnyCachedStationName } from "../util/stationIdentityCache";

// #3508 (station-monitor workstream) -- the "name this station" step. A station
// is one machine (the presenceInstanceId GUID, kept for the machine's life), and
// it is named ONCE: after sign-in, before show/ring setup. Kiosk doctrine: no
// skip, no dismiss, no escape hatch.
//
// Rules (each one is a test):
//   - no org yet (multi-org user still picking one in setup): pass through, the
//     name is per org and we cannot ask the server yet.
//   - the server says "never named" (404): show the name step. A tablet already
//     in use gets it once, on its next load.
//   - we could not ask (offline, timeout, 5xx): a tablet that is MID-SHOW (it
//     has a session AND scores waiting to sync) passes straight through and
//     stays through, never blocked by a network blip, and is named on its next
//     load. Any other tablet (a fresh one, or one with a session but nothing
//     in flight) gets a "can't reach the server" screen with a retry: the name
//     step cannot be skipped by unplugging the network.
//   - a browser that cannot keep the station GUID across loads is told so
//     rather than being silently re-named at every load.
//   - once a tablet has been let through it is never re-prompted in that page
//     load (no mid-session re-prompt).
const Full = ({ children, testId }) => (
  <Box
    data-testid={testId}
    sx={{
      position: "fixed",
      inset: 0,
      zIndex: (theme) => theme.zIndex.modal + 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
      p: 4,
      textAlign: "center",
      bgcolor: "background.default",
      color: "text.primary",
    }}
  >
    {children}
  </Box>
);

const WARN_DEBOUNCE_MS = 350;

// Latched (for this page load) once a tablet with a live session was let
// through because the server could not be asked. Module-level, not per
// instance: /setup and every scoring route mount their own gate, and a
// navigation must not re-ask (and spin) while the network is down.
let releasedThisLoad = false;
export function _resetStationSetupGateForTests() {
  releasedThisLoad = false;
}

function NameStep({ saveName, checkName }) {
  const { t } = useTranslation("score");
  // A known tablet meeting a new org starts from the name it already uses.
  const [name, setName] = useState(() => readAnyCachedStationName() || suggestStationName());
  const [isTest, setIsTest] = useState(false);
  const [matches, setMatches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const clean = cleanStationName(name);

  useEffect(() => {
    if (!clean) {
      setMatches([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const found = await checkName(clean);
      if (!cancelled) setMatches(found);
    }, WARN_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [clean, checkName]);

  const submit = async (e) => {
    e.preventDefault();
    if (!clean || saving) return;
    setSaving(true);
    setFailed(false);
    const ok = await saveName({ name: clean, isTest });
    if (!ok) {
      setFailed(true);
      setSaving(false);
    }
    // On success the hook flips to "named" and this component unmounts.
  };

  return (
    <Full testId="station-name-step">
      <Box component="form" onSubmit={submit} sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%", maxWidth: 420 }}>
        <Typography variant="h4" component="h1">
          {t("stationName.title")}
        </Typography>
        <Typography variant="body1">{t("stationName.help")}</Typography>
        <TextField
          label={t("stationName.label")}
          autoComplete="off"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
          slotProps={{ htmlInput: { maxLength: 40 } }}
        />
        {matches.length > 0 && (
          <Typography role="status" color="warning.main" data-testid="station-name-warning">
            {t("stationName.inUse", { suffixes: matches.map((m) => `…${m.idSuffix}`).join(", ") })}
          </Typography>
        )}
        <FormControlLabel
          control={<Checkbox checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />}
          label={t("stationName.testTablet")}
        />
        {failed && (
          <Typography role="alert" color="error" data-testid="station-name-failed">
            {t("stationName.saveFailed")}
          </Typography>
        )}
        <Button type="submit" variant="contained" size="large" disabled={!clean || saving}>
          {t("stationName.save")}
        </Button>
      </Box>
    </Full>
  );
}

// #3632: `keepMounted` is for /setup. Once this gate has let its children through
// (the station is named), an org change there must never swap InitialSetup out:
// the new org resolving shows nothing over the screen, and a name step that is
// genuinely needed (no name anywhere, name taken in the new org, refused) is a
// full-screen layer ON TOP of the still-mounted setup screen, so saving it lands
// back on setup with the org (and show) still picked.
export default function StationSetupGate({ children, keepMounted = false }) {
  const { t } = useTranslation("score");
  const { activeOrgId, orgResolved } = useActiveOrg();
  const { isSessionReady, pendingSync, unsyncedRideCount } = useScoreContext();
  const midShow = Boolean(isSessionReady && (pendingSync > 0 || unsyncedRideCount > 0));
  const { status, saveName, checkName, recheck } = useStationIdentity(activeOrgId);
  const passedThrough = useRef(false);
  if (status === "named") passedThrough.current = true;
  const stay = keepMounted && passedThrough.current;

  // `layer` is what blocks the app for this state (null = let it through). When
  // `stay`, the children are ALWAYS rendered in the same slot, with the layer
  // beside them, so a layer coming and going never remounts them.
  let layer = null;
  if (releasedThisLoad) {
    layer = null;
  } else if (!activeOrgId) {
    // No org yet. A first-ever load is still fetching the org list, or has it but
    // has not auto-picked yet (wait, so the scoring screen never flashes before
    // the name step). Once the org list is settled with no org at all there is
    // nothing to name the station under: let setup show its own message.
    if (!orgResolved) {
      layer = (
        <Full testId="station-name-loading">
          <CircularProgress aria-label={t("stationName.loading")} />
        </Full>
      );
    }
  } else if (status === "named") {
    layer = null;
  } else if (status === "unreachable" && midShow) {
    releasedThisLoad = true;
  } else if (status === "needs-name" && !canPersistStationIdentity()) {
    layer = (
      <Full testId="station-name-no-storage">
        <Typography variant="h5" component="h1">
          {t("stationName.noStorage")}
        </Typography>
      </Full>
    );
  } else if (status === "needs-name") {
    layer = <NameStep saveName={saveName} checkName={checkName} />;
  } else if (status === "unreachable") {
    layer = (
      <Full testId="station-name-unreachable">
        <Typography variant="h5" component="h1">
          {t("stationName.unreachable")}
        </Typography>
        <Button variant="contained" size="large" onClick={recheck}>
          {t("stationName.retry")}
        </Button>
      </Full>
    );
  } else if (!stay) {
    layer = (
      <Full testId="station-name-loading">
        <CircularProgress aria-label={t("stationName.loading")} />
      </Full>
    );
  }

  if (stay) {
    return (
      <>
        {children}
        {layer}
      </>
    );
  }
  return layer ?? children;
}
